import { useEffect, useRef, forwardRef, useImperativeHandle } from 'react';

const SCRIPT_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
const SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY;

let scriptPromise = null;
function loadTurnstileScript() {
  if (window.turnstile) return Promise.resolve();
  if (scriptPromise) return scriptPromise;
  scriptPromise = new Promise((resolve, reject) => {
    const s = document.createElement('script');
    s.src = SCRIPT_SRC;
    s.async = true;
    s.defer = true;
    s.onload = resolve;
    s.onerror = reject;
    document.head.appendChild(s);
  });
  return scriptPromise;
}

// Cloudflare Turnstile, used PROPERLY for forms: rendered idle (execution: 'execute',
// appearance: 'interaction-only') and the challenge runs ONLY when the form submits —
// call `ref.getToken()` in the submit handler. This means the token is always fresh
// (they expire in ~5 min if minted on page load) and Cloudflare stops warning about
// preloaded-but-unused challenge assets.
// Tokens are SINGLE-USE: after a failed submit, bump `resetSignal` so the next
// getToken() runs a fresh challenge instead of resending a dead token.
const TurnstileWidget = forwardRef(function TurnstileWidget({ onVerify, onExpire, resetSignal = 0 }, ref) {
  const containerRef = useRef(null);
  const widgetId = useRef(null);
  const tokenRef = useRef('');
  const waiters = useRef([]);
  // Keep latest callbacks in a ref so the widget renders once (no re-render churn).
  const cbs = useRef({ onVerify, onExpire });
  cbs.current = { onVerify, onExpire };

  const settle = (token) => {
    const pending = waiters.current.splice(0);
    pending.forEach((resolve) => resolve(token));
  };

  useImperativeHandle(ref, () => ({
    // Resolve with a token, running the challenge NOW (at submit time). Falls back
    // to '' when unconfigured/blocked — the backend decides whether to enforce.
    getToken() {
      if (tokenRef.current) return Promise.resolve(tokenRef.current);
      if (!SITE_KEY || widgetId.current == null || !window.turnstile) return Promise.resolve('');
      return new Promise((resolve) => {
        waiters.current.push(resolve);
        try {
          window.turnstile.execute(widgetId.current);
        } catch {
          settle('');
        }
        // Never hang a submit — give the challenge 30s, then let the backend judge.
        setTimeout(() => {
          const i = waiters.current.indexOf(resolve);
          if (i >= 0) {
            waiters.current.splice(i, 1);
            resolve(tokenRef.current || '');
          }
        }, 30000);
      });
    },
  }));

  useEffect(() => {
    if (resetSignal > 0 && widgetId.current != null && window.turnstile) {
      try {
        tokenRef.current = '';
        cbs.current.onExpire?.();
        window.turnstile.reset(widgetId.current); // back to idle; next getToken() re-executes
      } catch { /* noop */ }
    }
  }, [resetSignal]);

  useEffect(() => {
    if (!SITE_KEY) return undefined;
    let cancelled = false;
    loadTurnstileScript()
      .then(() => {
        if (cancelled || !containerRef.current || !window.turnstile) return;
        widgetId.current = window.turnstile.render(containerRef.current, {
          sitekey: SITE_KEY,
          theme: 'dark',
          execution: 'execute', // idle until getToken() — no challenge on page load
          appearance: 'interaction-only', // stays invisible unless Cloudflare needs a click
          callback: (token) => {
            tokenRef.current = token;
            cbs.current.onVerify?.(token);
            settle(token);
          },
          'expired-callback': () => {
            tokenRef.current = '';
            cbs.current.onExpire?.();
          },
          'error-callback': () => {
            tokenRef.current = '';
            cbs.current.onExpire?.();
            settle('');
          },
        });
      })
      .catch(() => {
        /* script blocked/offline — token stays empty; backend rejects if enforcement is on */
      });
    return () => {
      cancelled = true;
      if (widgetId.current != null && window.turnstile) {
        try { window.turnstile.remove(widgetId.current); } catch { /* noop */ }
      }
    };
  }, []);

  if (!SITE_KEY) return null;
  return <div ref={containerRef} className="my-3 flex justify-center empty:my-0" />;
});

export default TurnstileWidget;
